import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  return Object.keys(obj).reduce((acc, key) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    acc[camelKey] = toCamelCase(obj[key]);
    return acc;
  }, {});
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const loadProfile = async (userId) => {
    if (!userId || !supabase) return;
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        setUserProfile({
          id: userId,
          displayName: 'User',
          subscriptionStatus: 'free',
          role: 'user',
          isMigratedUser: false,
          profileRecompletedAt: null,
          onboardingCompletedAt: null,
        });
        return;
      }
      setUserProfile(toCamelCase(data));
    } catch (err) {
      setUserProfile({
        id: userId,
        displayName: 'User',
        subscriptionStatus: 'free',
        role: 'user',
        isMigratedUser: false,
        profileRecompletedAt: null,
        onboardingCompletedAt: null,
      });
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) loadProfile(session.user.id);
      else setUserProfile(null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) loadProfile(session.user.id);
      else setUserProfile(null);
    });
    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      const msg =
        error.message?.includes('Invalid') ? 'Email or password is incorrect' : error.message;
      return { data: null, error: { ...error, message: msg } };
    }
    return { data, error: null };
  };

  const signOut = async () => {
    if (!supabase) return { error: null };
    const { error } = await supabase.auth.signOut();
    setUser(null);
    setUserProfile(null);
    return { error };
  };

  const resetPasswordForEmail = async (email) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/login` : '';
    const { data, error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo,
    });
    if (error) return { data: null, error };
    return { data: true, error: null };
  };

  const updatePassword = async (newPassword) => {
    if (!supabase || !user) return { error: { message: 'Not logged in' } };
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { data: null, error };
    return { data: true, error: null };
  };

  const updateProfile = async (updates) => {
    if (!user || !supabase) return { error: { message: 'Not logged in' } };
    const snake = Object.keys(updates).reduce((acc, k) => {
      acc[k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)] = updates[k];
      return acc;
    }, {});
    const { data, error } = await supabase
      .from('user_profiles')
      .update(snake)
      .eq('id', user.id)
      .select()
      .single();
    if (error) return { data: null, error };
    setUserProfile(toCamelCase(data));
    return { data: toCamelCase(data), error: null };
  };

  const refreshProfile = () => {
    if (user?.id) return loadProfile(user.id);
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    refreshProfile,
    resetPasswordForEmail,
    updatePassword,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
